import { SessionEntityType, UserSession } from "../api/models/session";
import { ClassSubObject } from "../api/models/students";
import { encodeURLString, parseURL, URLDataType } from "../Transitions/ParseURL";
import { currentEnv, Env } from "../utils/env";
import fakeNames from "./fakeNames.json";
import { IslandDetails } from "./IslandManager";
import { StudentManager } from "./StudentManager";
import { TeacherManager } from "./TeacherManager";
import { UserManager } from "./UserManager";

function getIslandDetails(
  classroom: ClassSubObject,
  teacher?: { title?: string; lastName?: string; avatarURL?: string }
): IslandDetails {
  //Get the teacher title + last name or default to "Welcome to"
  let ownerName = "Welcome to";
  if (teacher && teacher.lastName != null) {
    ownerName = `${teacher.title?.trim() || ""} ${teacher.lastName.trim()}`.trim();
    ownerName = makePossessive(ownerName);
  }

  return {
    id: `class-${classroom._id}`,
    ownerName,
    ownerAvatarURL: teacher?.avatarURL ?? "",
    islandName: classroom.name,
  };
}

function makePossessive(name: string) {
  if (name.substring(name.length - 1) === "s") {
    name += "'";
  } else {
    name += "'s";
  }
  return name;
}

export function getClassIslands(entityType: SessionEntityType) {
  const islands: IslandDetails[] = [];
  if (entityType === "teacher") {
    for (const classroom of TeacherManager.instance.sortedClasses) {
      if (!classroom.archived) {
        const classOwner = classroom.collaborators.find((teacher) => teacher.sharingStatus === "owner");
        const islandOwner = classOwner && {
          title: classOwner.title,
          lastName: classOwner.lastName,
          avatarURL: classOwner._links?.photo?.href,
        };
        islands.push(getIslandDetails(classroom, islandOwner));
      }
    }
  } else {
    for (const renderedClass of StudentManager.instance.sortedClasses) {
      if (!renderedClass.archived || renderedClass.recentlyArchived) {
        islands.push(getIslandDetails(renderedClass, renderedClass.teacher));
      }
    }
  }
  return islands;
}

export function getPersonalIsland(): IslandDetails {
  const user = getUserNameInfo().shortName;
  const ownerAvatarURL =
    (UserManager.instance.user?.type === "student"
      ? UserManager.instance.user?.student?.avatarURL
      : UserManager.instance.user?.teacher?.avatarURL) || "";
  return {
    id: `personal-${UserManager.instance.userId ?? ""}`,
    ownerName: makePossessive(user),
    ownerAvatarURL,
    islandName: "Personal Island",
  };
}

export function findIsland(room: string, entityType: SessionEntityType): IslandDetails | undefined {
  const islands = getClassIslands(entityType);
  islands.push(getPersonalIsland());

  for (const island of islands) {
    if (island.id === room) {
      return island;
    }
  }
}

export type UserNameInfo = {
  shortName: string;
  fullName: string;
};

export function getUserNameInfo(session: UserSession | undefined = UserManager.instance.user): UserNameInfo {
  if (session?.type === "teacher") {
    const title = session.teacher.title || "";
    const { firstName, lastName } = getFirstLastName(session.teacher);

    //Assign to user
    const firstLastName = `${firstName} ${lastName}`.trim();
    const fullName = `${title} ${firstLastName}`.trim();
    const shortName = `${title} ${lastName || firstName}`.trim();

    return { fullName, shortName };
  } else if (session?.type === "student") {
    const { firstName, lastName } = getFirstLastName(session.student);

    //Assign to user
    const fullName = `${firstName} ${lastName}`.trim();

    let shortName: string;
    //Create an abbreviated version of first name with last initial in the ideal case
    if (firstName.length > 0) {
      shortName = firstName.trim();

      if (lastName.length > 0) {
        shortName += ` ${lastName[0]}`;
      }
    } else {
      shortName = lastName.trim();
    }

    return { fullName, shortName };
  }

  //TODO: Log error
  return { fullName: "Undefined User", shortName: "UUser" };
}

function getFirstLastName(entity: { _id: string; firstName: string; lastName: string }) {
  let firstName = (entity.firstName ?? "").trim();
  let lastName = (entity.lastName ?? "").trim();

  //If the user doesn't have a first or last name, use an adjective/noun to create a fake one for them
  if (firstName === "" && lastName === "") {
    //Index off of their ID to stay consistent in future sessions
    //Grab the first 8 digits of ID to ensure the number isn't crazy large
    const firstPartID = entity._id.substring(0, Math.min(8, entity._id.length - 1));

    //Convert from hex to decimal number
    const hash = parseInt(firstPartID, 16);

    //Index into the fake names
    firstName = fakeNames.firstNames[hash % fakeNames.firstNames.length];
    lastName = fakeNames.lastNames[(hash * 7) % fakeNames.lastNames.length];
  }

  firstName = capitalizeFirstLetter(firstName);
  lastName = capitalizeFirstLetter(lastName);

  return { firstName, lastName };
}

function capitalizeFirstLetter(name: string) {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

export function getLauncherStartupOptions(): {
  entityType: SessionEntityType;
  classId?: string;
  startupParams: string;
  URLdata?: URLDataType;
} {
  const URLdata = parseURL();
  const launchDojoIslandsAs = URLdata?.entityType === "teacher" ? "teacher" : "student";
  let classId = URLdata?.classId;
  if (classId && !classId.startsWith("class-") && !classId.startsWith("personal-")) {
    classId = `class-${classId}`;
  }
  if (currentEnv() !== Env.Prod) {
    console.log(`getLauncherStartupOptions(): entityType=[${launchDojoIslandsAs}] classId=[${classId}]`);
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  const startupParams = urlSearchParams.get("startupParams") || "";

  return {
    entityType: launchDojoIslandsAs,
    classId,
    startupParams,
    URLdata,
  };
}

export function getUpdatedLauncherStartupOptions(newClassId: string) {
  let URLdata = parseURL();
  if (!URLdata) {
    URLdata = { classId: newClassId };
  } else {
    URLdata = { classId: newClassId, entityType: URLdata.entityType };
  }

  const urlSearchParams = new URLSearchParams(window.location.search);
  urlSearchParams.set("data", encodeURLString(JSON.stringify(URLdata)));
  return urlSearchParams.toString();
}
